/**
 * useNotifications Hook
 * 
 * Manages in-app notifications with polling for unread count.
 * Provides functions to fetch notifications, mark as read, etc.
 */

import { useState, useEffect, useCallback, useRef } from "react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Polling interval for unread count (60 seconds)
const POLL_INTERVAL = 60000;

export function useNotifications() {
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const pollIntervalRef = useRef(null);

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await fetch(`${API}/notifications/unread-count`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.unread_count || 0);
      }
    } catch (err) {
      console.error("[Notifications] Failed to fetch unread count:", err);
    }
  }, []);

  // Fetch notifications list
  const fetchNotifications = useCallback(async (limit = 10, unreadOnly = false) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: limit.toString() });
      if (unreadOnly) {
        params.append("unread_only", "true");
      }
      
      const response = await fetch(`${API}/notifications?${params}`, {
        credentials: "include",
      });
      
      if (response.ok) {
        const data = await response.json();
        setNotifications(data.notifications || []);
        return data.notifications || [];
      } else {
        throw new Error("Failed to fetch notifications");
      }
    } catch (err) {
      console.error("[Notifications] Failed to fetch:", err);
      setError(err.message);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Mark single notification as read
  const markAsRead = useCallback(async (notificationId) => {
    // Optimistic update - REMOVE from list (since dropdown shows only unread)
    setNotifications(prev => prev.filter(n => n.notification_id !== notificationId));
    setUnreadCount(prev => Math.max(0, prev - 1));

    try {
      const response = await fetch(`${API}/notifications/${notificationId}/read`, {
        method: "POST",
        credentials: "include",
      });
      
      if (!response.ok) {
        // Revert on failure - refetch
        await fetchUnreadCount();
        await fetchNotifications(10, true);
      }
    } catch (err) {
      console.error("[Notifications] Failed to mark as read:", err);
      // Revert on failure
      await fetchUnreadCount();
      await fetchNotifications(10, true);
    }
  }, [fetchUnreadCount, fetchNotifications]);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    // Optimistic update - CLEAR list (since dropdown shows only unread)
    const previousNotifications = notifications;
    const previousUnread = unreadCount;
    setNotifications([]);
    setUnreadCount(0);

    try {
      const response = await fetch(`${API}/notifications/read-all`, {
        method: "POST",
        credentials: "include",
      });
      
      if (!response.ok) {
        // Revert on failure
        setNotifications(previousNotifications);
        setUnreadCount(previousUnread);
      }
    } catch (err) {
      console.error("[Notifications] Failed to mark all as read:", err);
      setNotifications(previousNotifications);
      setUnreadCount(previousUnread);
    }
  }, [notifications, unreadCount]);

  // Start polling on mount
  useEffect(() => {
    // Initial fetch
    fetchUnreadCount();

    // Set up polling
    pollIntervalRef.current = setInterval(fetchUnreadCount, POLL_INTERVAL);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [fetchUnreadCount]);

  return {
    unreadCount,
    notifications,
    loading,
    error,
    fetchUnreadCount,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
  };
}

export default useNotifications;
